import { api } from './api';

// Define a service using a base URL and expected endpoints for Dina Batch management
export const dinaBatchApi = api.injectEndpoints({
  reducerPath: 'dinaBatchApi',
  tagTypes: ['DinaBatches', 'DinaBatchTypes'],
  endpoints: (builder) => ({
    // Batch operations
    getAllBatches: builder.query({
      query: () => ({
        url: `/api/administration/batches`,
        method: 'GET'
      }),
      providesTags: ['DinaBatches']
    }),
    getBatchById: builder.query({
      query: (id) => ({
        url: `/api/administration/batches/${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'DinaBatches', id }]
    }),
    getBatchesByKnowledgeBaseCode: builder.query({
      query: (knowledgeBaseCode) => ({
        url: `/api/administration/batches/knowledgeBase/${knowledgeBaseCode}`,
        method: 'GET'
      }),
      providesTags: (result, error, knowledgeBaseCode) => [
        { type: 'DinaBatches', knowledgeBaseCode },
        'DinaBatches'
      ]
    }),
    getBatchesByTypeCode: builder.query({
      query: (batchTypeCode) => ({
        url: `/api/administration/batches/type/${batchTypeCode}`,
        method: 'GET'
      }),
      providesTags: (result, error, batchTypeCode) => [
        { type: 'DinaBatches', batchTypeCode },
        'DinaBatches'
      ]
    }),
    getScheduledBatches: builder.query({
      query: () => ({
        url: '/api/administration/batches/scheduled',
        method: 'GET'
      }),
      providesTags: ['DinaBatches']
    }),
    createBatch: builder.mutation({
      query: (dinaBatchRequest) => ({
        url: '/api/administration/batches',
        method: 'POST',
        body: dinaBatchRequest
      }),
      invalidatesTags: ['DinaBatches']
    }),
    updateBatch: builder.mutation({
      query: ({ id, dinaBatchRequest }) => ({
        url: `/api/administration/batches/${id}`,
        method: 'PATCH',
        body: dinaBatchRequest
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'DinaBatches', id }, 'DinaBatches']
    }),
    deleteBatch: builder.mutation({
      query: (id) => ({
        url: `/api/administration/batches/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'DinaBatches', id }, 'DinaBatches']
    }),
    executeBatch: builder.mutation({
      query: (id) => ({
        url: `/api/administration/batches/${id}/execute`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, id) => [{ type: 'DinaBatches', id }, 'DinaBatches']
    }),

    // Batch Type operations
    getAllBatchTypes: builder.query({
      query: () => ({
        url: '/api/administration/batches/types',
        method: 'GET'
      }),
      providesTags: ['DinaBatchTypes']
    }),
    getBatchTypeByCode: builder.query({
      query: (code) => ({
        url: `/api/administration/batches/types/${code}`,
        method: 'GET'
      }),
      providesTags: (result, error, code) => [{ type: 'DinaBatchTypes', code }]
    }),
    getBatchTypesByCodes: builder.query({
      query: (codes) => ({
        url: '/api/administration/batches/types/by-codes',
        method: 'GET',
        params: { codes }
      }),
      providesTags: ['DinaBatchTypes']
    }),

    // Monitoring endpoints
    getJobExecutionStatus: builder.query({
      query: (jobExecutionId) => ({
        url: `/api/administration/batches/executions/${jobExecutionId}/status`,
        method: 'GET'
      }),
      // Enable polling for running jobs (every 2 seconds)
      pollingInterval: (result) => {
        if (result?.data?.status === 'STARTED' || result?.data?.status === 'STARTING') {
          return 2000; // Poll every 2 seconds for running jobs
        }
        return 0; // No polling for completed/failed jobs
      },
      providesTags: (result, error, jobExecutionId) => [{ type: 'DinaBatches', jobExecutionId }]
    }),
    getRunningJobs: builder.query({
      query: () => ({
        url: '/api/administration/batches/executions/running',
        method: 'GET'
      }),
      // Poll every 5 seconds for running jobs overview
      pollingInterval: 5000,
      providesTags: ['DinaBatches']
    }),
    getRecentCompletedJobs: builder.query({
      query: () => ({
        url: '/api/administration/batches/executions/recent',
        method: 'GET'
      }),
      // Poll every 30 seconds for recent jobs
      pollingInterval: 30000,
      providesTags: ['DinaBatches']
    }),
    getJobExecutionHistory: builder.query({
      query: ({ jobName, limit = 20 }) => ({
        url: `/api/administration/batches/executions/job/${jobName}/history`,
        method: 'GET',
        params: { limit }
      }),
      providesTags: (result, error, { jobName }) => [{ type: 'DinaBatches', jobName }]
    }),
    getStepDetails: builder.query({
      query: (jobExecutionId) => ({
        url: `/api/administration/batches/executions/${jobExecutionId}/steps`,
        method: 'GET'
      }),
      // Poll every 3 seconds for step details of running jobs
      pollingInterval: () => {
        // This would need to be determined by checking if the job is running
        // For now, we'll use a conservative approach
        return 3000;
      },
      providesTags: (result, error, jobExecutionId) => [{ type: 'DinaBatches', jobExecutionId }]
    }),
    getSystemMetrics: builder.query({
      query: () => ({
        url: '/api/administration/batches/monitoring/metrics',
        method: 'GET'
      }),
      // Poll every 10 seconds for system metrics
      pollingInterval: 10000,
      providesTags: ['DinaBatches']
    }),
    getDashboardData: builder.query({
      query: () => ({
        url: '/api/administration/batches/monitoring/dashboard',
        method: 'GET'
      }),
      // Poll every 5 seconds for dashboard overview
      pollingInterval: 5000,
      providesTags: ['DinaBatches']
    }),
    getLatestExecutionForKnowledgeBase: builder.query({
      query: (knowledgeBaseCode) => ({
        url: `/api/administration/batchMonitoring/executions/knowledgeBase/${knowledgeBaseCode}/latest`,
        method: 'GET'
      }),
      providesTags: (result, error, knowledgeBaseCode) => [
        { type: 'DinaBatches', knowledgeBaseCode }
      ]
    }),
    getBatchExecutionHistory: builder.query({
      query: ({ batchId, page = 0, size = 20, sortBy = 'executionDate', sortDir = 'desc' }) => ({
        url: `/api/administration/batches/${batchId}/executions/history`,
        method: 'GET',
        params: { page, size, sort: `${sortBy},${sortDir}` }
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, { batchId }) => [
        { type: 'DinaBatches', batchId },
        'DinaBatches'
      ]
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllBatchesQuery,
  useGetBatchByIdQuery,
  useGetBatchesByKnowledgeBaseCodeQuery,
  useGetBatchesByTypeCodeQuery,
  useGetScheduledBatchesQuery,
  useCreateBatchMutation,
  useUpdateBatchMutation,
  useDeleteBatchMutation,
  useExecuteBatchMutation,
  useGetAllBatchTypesQuery,
  useGetBatchExecutionHistoryQuery,
  useGetBatchTypeByCodeQuery,
  useGetBatchTypesByCodesQuery,
  useGetJobExecutionStatusQuery,
  useGetRunningJobsQuery,
  useGetRecentCompletedJobsQuery,
  useGetJobExecutionHistoryQuery,
  useGetStepDetailsQuery,
  useGetSystemMetricsQuery,
  useGetDashboardDataQuery
} = dinaBatchApi;
